import * as XLSX from 'xlsx'

import { PERIOD_OPTIONS } from '../constants/dashboardPeriod.js'

/**
 * Format the period into a human-readable string.
 */
export function formatPeriodLabel(period, customRange) {
  if (period === 'custom') {
    if (customRange?.startDate && customRange?.endDate) {
      return `Custom Range (${customRange.startDate} to ${customRange.endDate})`
    }
    return 'Custom Date Range'
  }

  if (period === 'all') {
    return 'All Time'
  }

  const found = PERIOD_OPTIONS.find((opt) => opt.value === period)
  if (found) {
    return found.label
  }

  if (!period) return 'All Time'

  return period
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

/**
 * Capitalize the first letter of a string.
 */
export function capitalizeFirst(str) {
  if (!str || typeof str !== 'string') return ''
  const trimmed = str.trim()
  if (!trimmed) return ''
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
}

/**
 * Calculate percentage string safely: e.g. "25.5%" or "0.0%"
 */
export function calculatePercent(value, total) {
  const numVal = Number(value) || 0
  const numTot = Number(total) || 0
  if (numTot <= 0) return '0.0%'
  return `${((numVal / numTot) * 100).toFixed(1)}%`
}

/**
 * Format numbers with standard Indian/International thousands commas: e.g. "5,624,634"
 */
export function formatNumber(val) {
  if (val == null || val === '') return '0'
  const num = Number(val)
  if (isNaN(num)) return String(val)
  return num.toLocaleString('en-IN')
}

/**
 * Format a metric value with its unit in a clean, consistent, readable string.
 * Prevents Excel from right-aligning raw numbers into adjacent columns.
 */
export function formatMetric(val, unit = '') {
  if (val == null || val === '') return '—'

  const strVal = String(val).trim()

  // Percentages
  if (unit === 'Percentage' || strVal.endsWith('%')) {
    return strVal.endsWith('%') ? strVal : `${strVal}%`
  }

  // Currency (INR)
  if (unit === 'Currency' || unit === 'INR' || strVal.startsWith('₹')) {
    const rawNum = Number(strVal.replace(/[^0-9.-]/g, ''))
    if (!isNaN(rawNum)) {
      return `₹ ${rawNum.toLocaleString('en-IN')}`
    }
    return strVal
  }

  // Numbers with units
  const num = Number(strVal)
  if (!isNaN(num)) {
    const formattedNum = num.toLocaleString('en-IN')
    return unit ? `${formattedNum} ${unit}` : formattedNum
  }

  return unit ? `${strVal} ${unit}` : strVal
}

/**
 * Build standard professional header banner rows for export sheets.
 */
export function createReportHeader({
  title,
  subtitle,
  period,
  customRange,
  exportedAt,
}) {
  const periodLabel = formatPeriodLabel(period, customRange)
  const timestamp = exportedAt || new Date().toLocaleString()

  return [
    [`SUPERIOR CRM — ${title.toUpperCase()}`],
    [subtitle || 'Analytics & Performance Report'],
    ['Reporting Period:', periodLabel],
    ['Generated At:', timestamp],
    [], // Blank visual separator
  ]
}

/**
 * Auto-fit column widths so text and numbers never get clipped or show '###'.
 * Banners and single-cell section headers are excluded from inflating Column 0.
 */
export function calculateColumnWidths(rows = [], options = {}) {
  const { minWidth = 16, maxWidth = 55, skipHeaderRows = 4 } = options
  const widths = []

  rows.forEach((row, rowIndex) => {
    if (!Array.isArray(row)) return

    // Detect if this row is a title banner or section header with only 1 non-empty cell
    const filledCells = row.filter((c) => c != null && String(c).trim() !== '')
    const isSingleCellHeader = filledCells.length === 1

    row.forEach((cell, colIndex) => {
      // Skip the title banner and section title banners from inflating column 0 width
      if ((rowIndex < skipHeaderRows || isSingleCellHeader) && colIndex === 0) {
        return
      }

      const str = cell != null ? String(cell) : ''
      const len = str.length
      widths[colIndex] = Math.max(widths[colIndex] || 0, len)
    })
  })

  return widths.map((w) => ({
    wch: Math.min(Math.max((w || 0) + 4, minWidth), maxWidth),
  }))
}

/**
 * Create a sheet, apply auto-fit column widths, merge section titles, and append to workbook.
 */
export function appendStyledSheet(workbook, name, rows = [], options = {}) {
  const sheet = XLSX.utils.aoa_to_sheet(rows)
  sheet['!cols'] = calculateColumnWidths(rows, options)

  // Determine max number of columns
  let maxCols = 0
  rows.forEach((row) => {
    if (Array.isArray(row) && row.length > maxCols) {
      maxCols = row.length
    }
  })
  if (maxCols < 4) maxCols = 4

  // Automatically merge section titles across the table width
  const merges = []
  rows.forEach((row, rowIndex) => {
    if (!Array.isArray(row) || row.length === 0) return

    const filledCells = row.filter((c) => c != null && String(c).trim() !== '')
    if (
      filledCells.length === 1 &&
      row[0] != null &&
      String(row[0]).trim() !== ''
    ) {
      merges.push({
        s: { r: rowIndex, c: 0 },
        e: { r: rowIndex, c: maxCols - 1 },
      })
    }
  })

  if (merges.length > 0) {
    sheet['!merges'] = merges
  }

  XLSX.utils.book_append_sheet(workbook, sheet, name.slice(0, 31))
  return sheet
}
